import type { TItemQueryComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import { skipToken, useQuery, type UndefinedInitialQueryOptions } from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";
import { computed, toRef, toValue, unref } from "vue";
import type { MaybeRefOrGetter, Ref } from "vue";
import { useQueryEnabled } from "./useQueryEnabled";
import { logError } from "@/utils/AppError";
import { djangoViewsetErrorHandler } from "@/utils/DjangoError";
import { useAbility } from "@casl/vue";
import {
  DEFAULT_ITEM_NO_RETRY_STATUS_CODES,
  DEFAULT_STALE_TIME,
  mergeNoRetryStatusCodes,
  shouldSuppressQueryError,
  type TBaseQueryOptions,
} from "./baseQueryOptions";
import { useGenericQueryKey } from "./useGenericQueryKey";
import { JOB_ID_FILTER_KEY, useJobIdQueryParam } from "./useJobIdQueryParam";
import { useAccessCheckRedirect, useNotFoundRedirect } from "@/router/queryErrorRedirect";

/**
 * Configuration options for the `useGenericQuery` composable.
 *
 * @template TResponseItem - The type of the item in the response data.
 * @template TId - The type of the identifier (e.g. string, number, or object) used to fetch the item.
 */
export type TGenericItemQueryOptions<TResponseItem, TId = string> = Omit<
  TBaseQueryOptions,
  "params"
> & {
  /**
   * Item-specific composable parameters, including an `enabled` flag and the reactive `id`.
   * Accepts a plain object, a ref, or a getter function returning the params.
   */
  params: MaybeRefOrGetter<TItemQueryComposableParams<TId>>;
  /**
   * The asynchronous function that fetches the item data.
   *
   * Receives the unref'd `id`. With `jobSignal`, a second argument carries the resolved job as a
   * ready-to-spread query-param object (`{ job_id }`) — pass it straight to the service call. It
   * is `undefined` for every query that did not opt in, so existing fetchers are unaffected.
   */
  fetcher: (
    id: TId,
    jobParams?: { [JOB_ID_FILTER_KEY]: string | undefined }
  ) => Promise<AxiosResponse<TResponseItem>>;
  /**
   * When provided, runs against successful fetch results. Returning `false`
   * redirects to the unauthorized page (same as HTTP 403).
   */
  accessCheck?: (data: TResponseItem) => boolean;
  /**
   * Additional options to pass to Vue Query's `useQuery`.
   * These omit internal options completely managed by the composable (`queryKey`, `queryFn`, `enabled`, `initialData`).
   */
  options?: Omit<
    UndefinedInitialQueryOptions<TResponseItem, Error, [string, TId]>,
    "queryKey" | "queryFn" | "enabled" | "initialData" | "staleTime"
  >;
};

/**
 * A generic Vue Query composable to handle fetching a single item of data.
 * Wraps TanStack Query's `useQuery` to easily integrate API fetchers with reactive IDs,
 * permission checks, and unwrapping.
 *
 * @template TResponseItem - The type of the item returned.
 * @template TId - The type of the item identifier.
 *
 * @param {TGenericItemQueryOptions<TResponseItem, TId>} options - Configuration options for the item query.
 * @returns The Vue Query result object containing `data`, `isLoading`, `error`, etc.
 */
export const useGenericQuery = <TResponseItem, TId = string>(
  options: TGenericItemQueryOptions<TResponseItem, TId>
) => {
  const paramsRef = computed(() => toValue(options.params));
  const enabledRef = computed(() => toValue(paramsRef.value.enabled) ?? true);
  const idRef = computed(() => toValue(paramsRef.value.id)) as Ref<TId | undefined | null>;
  const queryKeyRef = toRef(options.queryKey) as Ref<readonly any[]>;
  const queryKey = useGenericQueryKey(queryKeyRef, idRef);
  const { can } = useAbility();

  // An item query has no filters object to fold the job into, so it gates here and reaches the
  // request through the fetcher's second argument.
  const jobId = useJobIdQueryParam(options.jobSignal);
  const jobParams = computed(() =>
    options.jobSignal ? { [JOB_ID_FILTER_KEY]: jobId.value } : undefined
  );

  const isEnabled = useQueryEnabled({
    enabled: computed(
      () => unref(enabledRef) && !!idRef.value && (!options.jobSignal || !!jobId.value)
    ),
    permission: options.permission,
    action: options.action,
    can: can,
    ignorePendingTermsBlock: options.ignorePendingTermsBlock,
  });

  const noRetryStatusCodes = mergeNoRetryStatusCodes(
    DEFAULT_ITEM_NO_RETRY_STATUS_CODES,
    options.noRetryStatusCodes
  );

  const fetchItem = async () => {
    try {
      if (!idRef.value) return {} as TResponseItem;
      return (await options.fetcher(idRef.value, jobParams.value)).data;
    } catch (error) {
      const safeStringify = (val: unknown) => {
        try {
          return JSON.stringify(toValue(val));
        } catch {
          return "[unserializable]";
        }
      };
      logError(error, {
        context: {
          feature: "query",
          step: "fetcher",
          queryKey: safeStringify(queryKeyRef),
          id: safeStringify(idRef),
        },
      });
      const suppressed =
        !!toValue(options.redirectOnNotFound) ||
        shouldSuppressQueryError(options.suppressErrors, error);
      if (!suppressed) djangoViewsetErrorHandler()(error);
      throw error;
    }
  };

  const query = useQuery<TResponseItem, Error>({
    queryKey: queryKey as any,
    // `enabled` only governs TanStack's *automatic* fetching; an explicit `refetch()` bypasses
    // it. Swapping in `skipToken` while disabled makes the query a genuine no-op — the fetcher
    // is never invoked — and it resumes automatically when `isEnabled` flips true.
    queryFn: computed(() => (isEnabled.value ? fetchItem : skipToken)) as any,
    enabled: isEnabled,
    staleTime: options.staleTime ?? DEFAULT_STALE_TIME,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
    refetchOnReconnect: options.refetchOnReconnect,
    ...(options.options ?? {}),
    retry: (failureCount: number, error: any) => {
      const status = error?.response?.status;
      if (status && noRetryStatusCodes.has(status)) {
        return false;
      }
      const retryOption = (options.options as any)?.retry;
      if (typeof retryOption === "function") return retryOption(failureCount, error);
      if (typeof retryOption === "number") return failureCount < retryOption;
      if (typeof retryOption === "boolean") return retryOption;
      return failureCount < 3;
    },
  });

  useNotFoundRedirect(query.error, {
    enabled: computed(() => !!toValue(options.redirectOnNotFound)),
  });

  useAccessCheckRedirect(query.data, options.accessCheck, {
    enabled: computed(() => !!options.accessCheck),
  });

  return query;
};
